import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@service-victoria/component-library/components/Button';
import Error from '@service-victoria/component-library/components/Notification/Error';
import Body from '@service-victoria/component-library/components/Text/Body';
import Heading from '@service-victoria/component-library/components/Text/Heading';
import React, { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Redirect, useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';

import InputField from 'Components/InputField';
import {
  CallToActionContainer,
  ContentContainer,
  Form
} from 'Components/KioskRegistration/KioskRegistration';
import Page from 'Components/Page';
import useDeviceRegistration from 'hooks/useDeviceRegistration';
import { constants } from 'utils';
import { trackEvent } from 'utils/analytics';
import { Routes } from 'utils/constants';

import { MAX_SECURITY_CODE_LENGTH, schema } from './schema';

interface FormValues {
  securityCode: string;
}

const Verify: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ transactionId?: string }>();
  const transactionId = location.state?.transactionId;

  const { registerDevice, authenticateDeviceWithOTP, registerDeviceResponse } =
    useDeviceRegistration({
      onAuthenticateDeviceWithOTPSuccess: () => {
        history.push(constants.Routes.RegistrationSuccess, { transactionId });
      }
    });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError
  } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if ([400, 401].includes(authenticateDeviceWithOTP.error?.response?.status ?? 0)) {
      setError('securityCode', {
        type: 'manual',
        message: constants.GenericValidationErrors.invalidSecurityOrKioskCode
      });
    }
  }, [authenticateDeviceWithOTP.error?.response?.status, setError]);

  if (!registerDeviceResponse) {
    return <Redirect to={Routes.Register} />;
  }

  const onSubmit: SubmitHandler<FormValues> = async data => {
    authenticateDeviceWithOTP.mutate({
      otp: data.securityCode,
      locationId: registerDeviceResponse.locationId
    });
    trackEvent({
      label: 'submitOTP',
      action: 'KioskAdmin'
    });
  };

  const navigateToRegisterPage = () => history.push(constants.Routes.Register, { transactionId });

  const resendCode = () => {
    registerDevice.mutate({
      kioskCode: registerDeviceResponse.kioskCode
    });
    trackEvent({
      label: 'resendOTP',
      action: 'KioskAdmin'
    });
  };

  const isAsyncRequestLoading = authenticateDeviceWithOTP.isLoading || registerDevice.isLoading;

  return (
    <Page banner={{ title: constants.Page.registrationTitle }}>
      <Heading.H2 fontWeight={600} data-testid='PageHeading'>
        Hi {registerDeviceResponse.businessName}
      </Heading.H2>
      <ContentContainer>
        <Body data-testid='PageBody'>
          We’ve sent a security code to {registerDeviceResponse.email}. It lasts 60 minutes.
        </Body>
        <Body data-testid='PageBody'>We do this to make sure it’s really you.</Body>
        <Heading.H3 fontWeight={600} as='span' data-testid='PageHeading3'>
          This device will be registered for the following location
        </Heading.H3>
        <Body fontWeight={600} data-testid='PageBodyLocation'>
          Location: {registerDeviceResponse.locationName}
        </Body>
        {registerDeviceResponse.areaName && (
          <Body fontWeight={600} data-testid='PageBodyAreaName'>
            Area: {registerDeviceResponse.areaName}
          </Body>
        )}
      </ContentContainer>
      <Form onSubmit={handleSubmit(onSubmit)} action='#' data-testid='Form'>
        <InputField
          {...register('securityCode', {
            required: true
          })}
          disabled={isAsyncRequestLoading}
          label='Security code'
          hint='This code lasts 60 minutes'
          error={errors.securityCode?.message}
          maxLength={MAX_SECURITY_CODE_LENGTH}
          style={{ textTransform: 'uppercase' }}
          autoCapitalize='characters'
        />
        {((authenticateDeviceWithOTP.error?.response?.status ?? 0) >= 500 ||
          (registerDevice.error?.response?.status ?? 0) >= 500) &&
          !isSubmitting && (
            <div data-testid='OTPNetworkError'>
              <Error.Text title='Oops! Something went wrong!' value='Please try again shortly' />
            </div>
          )}
        {authenticateDeviceWithOTP.error?.response?.status === 404 && !isSubmitting && (
          <div data-testid='OTPCodeExpiredError'>
            <Error.Field title='Sorry, this code has expired' value='Resend code' />
          </div>
        )}
        <CallToActionContainer>
          <Button
            type='submit'
            disabled={isAsyncRequestLoading}
            isLoading={authenticateDeviceWithOTP.isLoading}
            data-testid='OTPSubmitButton'>
            Verify
          </Button>
          <Button
            buttonType='secondary'
            onClick={resendCode}
            disabled={isAsyncRequestLoading}
            isLoading={registerDevice.isLoading}
            data-testid='OTPResendCodeButton'>
            Resend code
          </Button>
          <Button
            buttonType='secondary'
            onClick={navigateToRegisterPage}
            disabled={isAsyncRequestLoading}
            data-testid='OTPBackButton'>
            Back
          </Button>
        </CallToActionContainer>
      </Form>
    </Page>
  );
};

export default Verify;
